import React, { useState } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { Package, ArrowRight, Sparkles, User, FileText, Target } from 'lucide-react';
import Card from '../components/ui/Card';
import { createDefaultProjectMetadata } from '../utils/validation';

const ProjectCreator: React.FC = () => {
    const { navigate, importProjectData } = useAppContext();
    const [step, setStep] = useState(1);
    const [projectName, setProjectName] = useState('');
    const [description, setDescription] = useState('');
    const [requirements, setRequirements] = useState('');
    const [author, setAuthor] = useState('Current User');

    const handleCreateProject = async (runPrime: boolean) => {
        if (!projectName.trim()) {
            alert("Please enter a project name.");
            return;
        }

        const metadata = createDefaultProjectMetadata(projectName, author);
        metadata.description = description;

        const reqsList = requirements.split('\n').filter(r => r.trim()).map((r, i) => ({
            id: `REQ-${i + 1}`,
            requirementText: r.trim(),
            type: 'Functional',
            source: 'User'
        }));

        const projectContext = {
            customerName: 'Default Customer',
            productName: projectName,
            productDescription: description,
            objectives: requirements.split('\n').filter(r => r.trim()),
        };

        const initialData = {
            projectMetadata: metadata,
            projectContext: projectContext,
            customerRequirements: reqsList,
            iterationHistory: [],
            fmeaItems: [],
            pDiagramList: [],
            bomItems: [],
            dvprTests: [],
            controls: []
        };

        // Initialize workspace
        await importProjectData(initialData as any, true);

        if (runPrime) {
            navigate('/magnus-prime');
        } else {
            navigate('/dashboard');
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8 py-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="text-center space-y-2">
                <div className="inline-flex p-3 bg-primary/10 rounded-2xl text-primary mb-2">
                    <Package size={32} />
                </div>
                <h1 className="text-4xl font-bold tracking-tight text-text-primary">Create New Project</h1>
                <p className="text-text-secondary text-lg max-w-lg mx-auto">
                    Initialize a new reliability workspace with standardized metadata and requirements.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                {[1, 2, 3].map((s) => (
                    <div key={s} className="flex flex-col gap-2">
                        <div className={`h-1.5 rounded-full transition-all duration-500 ${step >= s ? 'bg-primary' : 'bg-border'}`} />
                        <span className={`text-[10px] uppercase font-bold tracking-wider ${step >= s ? 'text-primary' : 'text-text-secondary'}`}>
                            Step {s}: {s === 1 ? 'Identity' : s === 2 ? 'Context' : 'Strategy'}
                        </span>
                    </div>
                ))}
            </div>

            <Card className="shadow-2xl border-primary/10">
                {step === 1 && (
                    <div className="space-y-6 animate-in fade-in duration-300">
                        <div className="space-y-4">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                <User className="text-primary" size={20} /> Project Identity
                            </h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1.5">Project Name</label>
                                    <input
                                        type="text"
                                        value={projectName}
                                        onChange={e => setProjectName(e.target.value)}
                                        placeholder="e.g. NextGen Electric Motor"
                                        className="w-full p-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-all text-lg font-medium"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1.5">Primary Author / Engineer</label>
                                    <input
                                        type="text"
                                        value={author}
                                        onChange={e => setAuthor(e.target.value)}
                                        className="w-full p-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary transition-all"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <button
                                onClick={() => setStep(2)}
                                disabled={!projectName.trim()}
                                className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 disabled:opacity-50 shadow-lg shadow-primary/20 transition-all"
                            >
                                Next Step <ArrowRight size={18} />
                            </button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-6 animate-in fade-in duration-300">
                        <div className="space-y-4">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                <FileText className="text-primary" size={20} /> System Context
                            </h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1.5">Description</label>
                                    <textarea
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                        placeholder="Briefly describe the system and its environment..."
                                        rows={3}
                                        className="w-full p-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary transition-all"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1.5 flex justify-between">
                                        Initial Requirements
                                        <span className="text-xs text-text-secondary">(One per line)</span>
                                    </label>
                                    <textarea
                                        value={requirements}
                                        onChange={e => setRequirements(e.target.value)}
                                        placeholder="Must operate at 500 RPM&#10;Must survive 1.5m drop test&#10;Power consumption < 50W"
                                        rows={6}
                                        className="w-full p-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary transition-all font-mono text-sm"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-between">
                            <button onClick={() => setStep(1)} className="px-6 py-3 text-text-secondary hover:text-text-primary">Back</button>
                            <button
                                onClick={() => setStep(3)}
                                className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all"
                            >
                                Finalize <ArrowRight size={18} />
                            </button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="space-y-8 animate-in fade-in duration-300">
                        <div className="text-center space-y-2">
                            <h2 className="text-2xl font-bold tracking-tight">Select Initiation Strategy</h2>
                            <p className="text-text-secondary">How would you like to start building this project?</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <button
                                onClick={() => handleCreateProject(true)}
                                className="flex flex-col items-center p-8 bg-gradient-to-br from-primary to-indigo-700 text-white rounded-2xl hover:scale-[1.02] hover:shadow-2xl transition-all group relative overflow-hidden"
                            >
                                <div className="absolute top-0 right-0 p-4 opacity-10">
                                    <Sparkles size={120} />
                                </div>
                                <Sparkles className="mb-4" size={48} />
                                <span className="text-xl font-bold mb-1">Magnus Prime</span>
                                <span className="text-sm opacity-80 text-center">Run 7-Epoch AI simulation to generate full structural & risk data.</span>
                            </button>

                            <button
                                onClick={() => handleCreateProject(false)}
                                className="flex flex-col items-center p-8 bg-background border-2 border-border rounded-2xl hover:border-primary hover:bg-primary/5 hover:scale-[1.02] transition-all group"
                            >
                                <ArrowRight className="mb-4 text-text-secondary group-hover:text-primary transition-colors" size={48} />
                                <span className="text-xl font-bold mb-1">Manual Setup</span>
                                <span className="text-sm text-text-secondary text-center">Open a blank workspace and define functions & FMEAs manually.</span>
                            </button>
                        </div>

                        <div className="bg-muted/30 p-4 rounded-xl flex gap-3 items-start border border-border">
                            <Target className="text-primary mt-1" size={20} />
                            <div className="text-xs text-text-secondary leading-relaxed">
                                <strong>Project Summary:</strong> {projectName} will be initialized with {requirements.split('\n').filter(r => r.trim()).length} requirements.
                                Standardized metadata (v2.0.0) will be attached for downstream traceability across all modules.
                            </div>
                        </div>

                        <div className="flex justify-center">
                            <button onClick={() => setStep(2)} className="text-sm text-text-secondary hover:underline">Change details</button>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default ProjectCreator;
